import { Clipboard } from '@angular/cdk/clipboard';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { CopySelectionPopupComponent } from '../components/copy-selection-popup/copy-selection-popup.component';

const INTERACTIVE = 'button, input, select, textarea, mat-checkbox, [role=menuitem]';
const FIELDS = 'input, textarea, select';
const FEEDBACK_MS = 1200;
const LINK_CLICK_DELAY_MS = 250;
const EXTEND_KEYS = new Set(['Shift', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

interface Dismissed {
  text: string;
  anchorNode: Node | null;
  anchorOffset: number;
  focusNode: Node | null;
  focusOffset: number;
}

@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appCopyOnSelect]'
})
export class CopyOnSelectDirective implements OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);
  private readonly overlay = inject(Overlay);
  private readonly clipboard = inject(Clipboard);

  private overlayRef: OverlayRef | null = null;
  private copySub: Subscription | null = null;
  private text = '';
  private dismissed: Dismissed | null = null;
  private linkTimer: ReturnType<typeof setTimeout> | null = null;
  private replaying = false;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly teardown: (() => void)[] = [];

  constructor() {
    this.zone.runOutsideAngular(() => {
      this.listen(this.host, 'click', (e) => this.onClick(e as MouseEvent), true);
      this.listen(this.host, 'dblclick', () => this.cancelLinkClick(), true);
      this.listen(this.host, 'mouseup', () => this.detectLater());
      this.listen(this.host, 'keyup', (e) => this.onKeyUp(e as KeyboardEvent));
      this.listen(document, 'selectionchange', () => this.onSelectionChange());
      this.listen(document, 'mousedown', (e) => this.onDocumentMouseDown(e as MouseEvent), true);
      this.listen(document, 'keydown', (e) => (e as KeyboardEvent).key === 'Escape' && this.dismiss());
      this.listen(window, 'scroll', () => this.close(), true);
      this.listen(window, 'resize', () => this.close());
    });
  }

  ngOnDestroy(): void {
    this.cancelLinkClick();
    this.close();
    this.teardown.forEach((fn) => fn());
    this.teardown.length = 0;
  }

  private listen(target: EventTarget, type: string, fn: (e: Event) => void, capture = false): void {
    target.addEventListener(type, fn, capture);
    this.teardown.push(() => target.removeEventListener(type, fn, capture));
  }

  private later(fn: () => void, ms = 0): void {
    const id = setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
  }

  /** Returns the highlighted range when it is non-blank and fully inside the host, else null. */
  private validSelection(): { range: Range; text: string } | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const text = sel.toString();
    if (!text.trim()) return null;
    const range = sel.getRangeAt(0);
    if (!this.host.contains(range.commonAncestorContainer)) return null;
    const anchor = sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode?.parentElement;
    if (anchor?.closest(FIELDS)) return null;
    const active = document.activeElement;
    if (active?.matches(FIELDS) && this.host.contains(active)) return null;
    return { range, text };
  }

  private onKeyUp(event: KeyboardEvent): void {
    const selectAll = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a';
    if (EXTEND_KEYS.has(event.key) || selectAll) this.detectLater();
  }

  /** Closes the popup and remembers the selection so it is not reopened until it changes. */
  private dismiss(): void {
    const sel = window.getSelection();
    const found = this.validSelection();
    this.dismissed =
      sel && found
        ? {
            text: found.text,
            anchorNode: sel.anchorNode,
            anchorOffset: sel.anchorOffset,
            focusNode: sel.focusNode,
            focusOffset: sel.focusOffset
          }
        : null;
    this.close();
  }

  private isDismissed(): boolean {
    const d = this.dismissed;
    const sel = window.getSelection();
    return (
      !!d &&
      !!sel &&
      sel.toString() === d.text &&
      sel.anchorNode === d.anchorNode &&
      sel.anchorOffset === d.anchorOffset &&
      sel.focusNode === d.focusNode &&
      sel.focusOffset === d.focusOffset
    );
  }

  private onClick(event: MouseEvent): void {
    if (this.replaying) return;
    const target = event.target as Element | null;
    if (target?.closest?.(INTERACTIVE)) return;
    if (this.validSelection()) {
      this.cancelLinkClick();
      event.stopPropagation();
      return;
    }
    if (target?.closest?.('mic-link')) this.deferLinkClick(event, target);
  }

  /** Link cells react to the first click of a double-click, so hold it back until no second click follows. */
  private deferLinkClick(event: MouseEvent, target: Element): void {
    event.stopPropagation();
    this.cancelLinkClick();
    if (event.detail > 1) return;
    this.linkTimer = setTimeout(() => {
      this.linkTimer = null;
      if (this.validSelection() || !target.isConnected) return;
      this.replaying = true;
      try {
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      } finally {
        this.replaying = false;
      }
    }, LINK_CLICK_DELAY_MS);
  }

  private cancelLinkClick(): void {
    if (this.linkTimer) clearTimeout(this.linkTimer);
    this.linkTimer = null;
  }

  private detectLater(): void {
    this.later(() => {
      const found = this.validSelection();
      if (!found || this.isDismissed()) return;
      const rects = found.range.getClientRects();
      const rect = rects.length ? rects[rects.length - 1] : found.range.getBoundingClientRect();
      this.text = found.text;
      this.zone.run(() => this.open(rect));
    });
  }

  private onSelectionChange(): void {
    if (!this.validSelection()) this.dismissed = null;
    if (this.overlayRef && !this.validSelection()) this.zone.run(() => this.close());
  }

  private onDocumentMouseDown(event: MouseEvent): void {
    if (!this.overlayRef) return;
    if (this.overlayRef.overlayElement.contains(event.target as Node)) return;
    this.zone.run(() => this.close());
  }

  private open(rect: DOMRect): void {
    this.close();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo({ x: rect.left, y: rect.top, width: rect.width, height: rect.height })
      .withPositions([
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -6 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 6 }
      ])
      .withPush(true);
    this.overlayRef = this.overlay.create({ positionStrategy });
    const popup = this.overlayRef.attach(new ComponentPortal(CopySelectionPopupComponent)).instance;
    this.copySub = popup.copyRequested.subscribe(() => this.copy(popup));
  }

  private copy(popup: CopySelectionPopupComponent): void {
    if (this.clipboard.copy(this.text)) {
      popup.icon.set('check');
      popup.message.set('Copied');
      this.later(() => this.dismiss(), FEEDBACK_MS);
    } else {
      popup.icon.set('error_outline');
      popup.message.set('Copy failed, press Ctrl+C');
    }
  }

  private close(): void {
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.copySub?.unsubscribe();
    this.copySub = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
